// iter604 — Quick Branch Printer Setup
//
// User feedback: "all printers in a branch are on the same network, I
// just want to set a default printer and (optionally) a kitchen
// printer". This dialog does exactly that — pick a branch, enter
// receipt IP, optionally enter kitchen IP, click Save. Backend creates
// both rows, auto-marks each as the default for its role, and the POS
// auto-routes from there.

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Wifi, Loader2, ChefHat, Receipt as ReceiptIcon, CheckCircle2, XCircle, Printer } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { isBridgeAlive, pingPrinter, printReceipt as bridgePrint } from '@/lib/printBridge';

export default function QuickPrinterSetupDialog({ open, onOpenChange, onCreated }) {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('__all__');
  const [receiptIp, setReceiptIp] = useState('');
  const [kitchenIp, setKitchenIp] = useState('');
  const [paperWidth, setPaperWidth] = useState('80mm');
  const [port, setPort] = useState(9100);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  // Live ping results when the bridge is running — lets the user know
  // the IP they typed actually reaches a printer before they hit Save.
  const [pingState, setPingState] = useState({ receipt: null, kitchen: null });
  const [pinging, setPinging] = useState({ receipt: false, kitchen: false });

  // Fetch the branch list whenever the dialog opens — async fetch in
  // a tiny IIFE, mirrors the pattern in PrinterSettingsDialog. Form
  // resets are handled by the `key` prop the parent passes so they
  // never need their own effect (which the strict hooks lint rejects).
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
        const { data } = await api.get('/branches', { headers: { Authorization: `Bearer ${token}` } });
        setBranches(Array.isArray(data) ? data : []);
      } catch { setBranches([]); }
    })();
  }, [open]);

  const probe = async (which, ip) => {
    if (!ip) return;
    setPinging((s) => ({ ...s, [which]: true }));
    const alive = await isBridgeAlive();
    if (!alive) {
      setPinging((s) => ({ ...s, [which]: false }));
      toast.info('Bridge not running on this PC — saved without ping.');
      return;
    }
    const r = await pingPrinter(ip, port || 9100);
    setPingState((s) => ({ ...s, [which]: r.ok ? 'ok' : 'fail' }));
    setPinging((s) => ({ ...s, [which]: false }));
  };

  const save = async () => {
    if (!receiptIp.trim()) {
      toast.error('Receipt printer IP is required');
      return;
    }
    setBusy(true);
    try {
      const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
      const { data } = await api.post(
        '/cashier/printers/quick-setup',
        {
          branch_id: branchId === '__all__' ? null : branchId,
          paper_width: paperWidth,
          port,
          receipt_ip: receiptIp.trim(),
          kitchen_ip: kitchenIp.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const count = (data?.created || []).length;
      toast.success(`${count} printer${count === 1 ? '' : 's'} added for ${data?.branch_label || 'this branch'}`);
      onCreated?.(data?.created || []);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  // iter605 — fire a real test ticket to each typed-in IP via the
  // local bridge so the manager can confirm paper actually comes out
  // BEFORE saving. We don't need a backend round-trip for this; the
  // bridge accepts an inline payload + IP.
  const tryPrintNow = async () => {
    if (!receiptIp.trim()) {
      toast.error('Enter the receipt printer IP first');
      return;
    }
    setTesting(true);
    try {
      const alive = await isBridgeAlive();
      if (!alive) {
        // iter610 — Browser fallback: render a simple test sheet to a
        // hidden iframe and let the OS print dialog handle delivery.
        // Works on iPad (AirPrint), Android Chrome, and any desktop.
        const renderTest = (ip, role) => {
          const html = `<!doctype html><html><head><style>
            @page { size: 80mm auto; margin: 2mm; }
            body { font-family: monospace; width: 76mm; padding: 4mm; font-size: 12px; }
            .c { text-align: center; }
            hr { border: 0; border-top: 1px dashed #000; }
          </style></head><body>
            <div class="c" style="font-weight:bold">TEST TICKET${role === 'kitchen' ? ' — KITCHEN' : ''}</div>
            <div class="c">${ip}</div>
            <hr/>
            <div>If you can read this, this printer is wired up correctly.</div>
            <hr/>
            <div class="c" style="font-size:10px;color:#666">Quick Setup test · ${new Date().toLocaleString()}</div>
            <script>window.onload=()=>{setTimeout(()=>{window.print();},150)}</script>
          </body></html>`;
          const f = document.createElement('iframe');
          f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
          f.srcdoc = html;
          document.body.appendChild(f);
          setTimeout(() => { try { f.remove(); } catch { /* */ } }, 30000);
        };
        renderTest(receiptIp.trim(), 'receipt');
        if (kitchenIp.trim()) {
          // Tiny stagger so the second print dialog doesn't get
          // swallowed by the first on iPad.
          setTimeout(() => renderTest(kitchenIp.trim(), 'kitchen'), 800);
        }
        toast.info('No bridge running — opened the OS print dialog. Pick your AirPrint printer once.');
        return;
      }
      const stamp = new Date().toLocaleString();
      const baseContent = {
        title: 'TEST TICKET',
        company: 'SmartOERP',
        branch: 'Quick Setup test',
        order_number: 'TEST',
        date: stamp,
        items: [{ name: 'Quick Setup connection test', qty: 1, subtotal: 0 }],
        subtotal: 0, tax: 0, total: 0, currency: '',
        footer: 'If you can read this, the printer is wired up correctly!',
      };
      // Receipt printer.
      const rPrinter = { ip_address: receiptIp.trim(), port: port || 9100, paper_width: paperWidth, copies: 1, type: 'receipt' };
      const r1 = await bridgePrint(rPrinter, baseContent, { kitchen: false });
      if (r1.ok) toast.success(`Receipt printer ${receiptIp} — paper out ✅`);
      else toast.error(`Receipt printer ${receiptIp} — ${r1.data?.error || 'unreachable'}`);
      // Kitchen printer (optional).
      if (kitchenIp.trim()) {
        const kPrinter = { ip_address: kitchenIp.trim(), port: port || 9100, paper_width: paperWidth, copies: 1, type: 'kitchen' };
        const kContent = { ...baseContent, title: 'KITCHEN TEST' };
        const r2 = await bridgePrint(kPrinter, kContent, { kitchen: true });
        if (r2.ok) toast.success(`Kitchen printer ${kitchenIp} — paper out ✅`);
        else toast.error(`Kitchen printer ${kitchenIp} — ${r2.data?.error || 'unreachable'}`);
      }
    } finally {
      setTesting(false);
    }
  };

  const pingPill = (state) => {
    if (state === 'ok') return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold flex items-center gap-1"><CheckCircle2 size={9} /> Reachable</span>;
    if (state === 'fail') return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold flex items-center gap-1"><XCircle size={9} /> Unreachable</span>;
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="quick-printer-setup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ReceiptIcon size={18} className="text-orange-500" />
            Quick branch printer setup
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Type the IPs of your printers (they&apos;re both on the same Wi-Fi as
          your POS device). We&apos;ll mark the receipt printer as the default
          for this branch, and route kitchen tickets to the kitchen
          printer automatically.
        </p>

        <div className="space-y-3 mt-1">
          <div>
            <Label className="text-xs">Branch</Label>
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="rounded-lg" data-testid="quick-branch-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All branches (shared)</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Receipt printer */}
          <div className="rounded-xl border bg-violet-50/40 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <ReceiptIcon size={14} className="text-violet-600" />
                <span className="text-xs font-semibold">Receipt printer (default)</span>
              </div>
              {pingPill(pingState.receipt)}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="192.168.1.100"
                value={receiptIp}
                onChange={(e) => setReceiptIp(e.target.value)}
                className="rounded-lg flex-1"
                data-testid="quick-receipt-ip"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-lg shrink-0"
                onClick={() => probe('receipt', receiptIp.trim())}
                disabled={!receiptIp.trim() || pinging.receipt}
                data-testid="quick-ping-receipt"
              >
                {pinging.receipt ? <Loader2 size={13} className="animate-spin" /> : <Wifi size={13} />}
              </Button>
            </div>
          </div>

          {/* Kitchen printer (optional) */}
          <div className="rounded-xl border bg-orange-50/40 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <ChefHat size={14} className="text-orange-600" />
                <span className="text-xs font-semibold">Kitchen printer <span className="text-muted-foreground font-normal">(optional)</span></span>
              </div>
              {pingPill(pingState.kitchen)}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="192.168.1.101 (leave blank if none)"
                value={kitchenIp}
                onChange={(e) => setKitchenIp(e.target.value)}
                className="rounded-lg flex-1"
                data-testid="quick-kitchen-ip"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-lg shrink-0"
                onClick={() => probe('kitchen', kitchenIp.trim())}
                disabled={!kitchenIp.trim() || pinging.kitchen}
                data-testid="quick-ping-kitchen"
              >
                {pinging.kitchen ? <Loader2 size={13} className="animate-spin" /> : <Wifi size={13} />}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Leave empty if this branch doesn&apos;t have a separate kitchen
              printer. Receipts will still print to the receipt printer.
            </p>
          </div>

          {/* Paper + port */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Paper width</Label>
              <Select value={paperWidth} onValueChange={setPaperWidth}>
                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="58mm">58mm</SelectItem>
                  <SelectItem value="80mm">80mm</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Port</Label>
              <Input
                type="number"
                value={port}
                onChange={(e) => setPort(parseInt(e.target.value) || 9100)}
                className="rounded-lg"
                data-testid="quick-port-input"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between gap-2 mt-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={tryPrintNow}
            disabled={testing || !receiptIp.trim()}
            className="rounded-lg"
            data-testid="quick-setup-test-print"
          >
            {testing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Printer size={14} className="mr-1.5" />}
            Try printing now
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-lg" disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={busy || !receiptIp.trim()}
              className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="quick-setup-save"
            >
              {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
              Save printers
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
